import api from "../client";
import {
  malnutritionUseMocks,
  mockGetMalnutritionList,
  mockGetMalnutritionRiskAssessment,
  mockGetPatientWeights,
  mockSetMalnutritionFeedback,
} from "./mocks";

import type {
  MalnutritionFeedbackPayload,
  MalnutritionRiskAssessment,
  PatientWeightHistory,
} from "../../types/Malnutrition";
import type MalnutritionListEntry from "../../types/MalnutritionListEntry";

// One board: `group`, or `department` + `qvHospitalID`.
export const getMalnutritionList = async (
  department?: string,
  group?: string,
  qvHospitalID?: number
): Promise<MalnutritionListEntry[]> => {
  if (malnutritionUseMocks()) return mockGetMalnutritionList(department, group);
  const response = await api.get("/malnutrition-list/", {
    params: { department, group, qv_hospital_id: qvHospitalID },
  });
  return response?.data || [];
};

// Both endpoints answer "nothing for this patient" with DRF's Response(None),
// an empty body that axios returns as "" — so map it to null here.
export const getMalnutritionRiskAssessment = async (
  emrPatientID: string,
  emrHospitalID: string
): Promise<MalnutritionRiskAssessment | null> => {
  if (malnutritionUseMocks())
    return mockGetMalnutritionRiskAssessment(emrPatientID);
  const response = await api.get("/patient-malnutrition/", {
    params: { emr_patient_id: emrPatientID, emr_hospital_id: emrHospitalID },
  });
  return response?.data || null;
};

export const getPatientWeights = async (
  emrPatientID: string,
  emrHospitalID: string
): Promise<PatientWeightHistory | null> => {
  if (malnutritionUseMocks()) return mockGetPatientWeights(emrPatientID);
  const response = await api.get("/patient-recent-weights/", {
    params: { emr_patient_id: emrPatientID, emr_hospital_id: emrHospitalID },
  });
  return response?.data || null;
};

// Records one step of the answer: the Yes/No choice, or the "which criteria
// didn't apply?" follow-up, which resends the No it follows. The click omits
// the follow-up's fields, which the server leaves untouched.
export const postMalnutritionFeedback = async (
  emrPatientID: string,
  emrHospitalID: string,
  payload: MalnutritionFeedbackPayload
): Promise<void> => {
  // In mock mode there's no backend to persist to; record it locally so the
  // read-back reflects it, then acknowledge without a network call.
  if (malnutritionUseMocks()) {
    mockSetMalnutritionFeedback(emrPatientID, payload);
    return;
  }
  await api.post("/patient-malnutrition-feedback/", {
    emr_patient_id: emrPatientID,
    emr_hospital_id: emrHospitalID,
    ...payload,
  });
};
